import { FloatingToolbar } from "@note-app/ui/floating-toolbar";
import { HANDWRITING_FONT } from "@note-app/typography";
import { lazy, Suspense } from "react";
import { createCanvasNode } from "../domain/canvas";
import { diagramTemplate } from "@note-app/diagrams";
const EngineeringDiagramEditor = lazy(() => import("@note-app/diagrams/editor").then((module) => ({ default: module.DiagramEditor })));
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { CanvasChangeBatch, CanvasEditor } from "../controller/types";
import type {
  CanvasLoadState,
  CanvasTool,
  EntityId,
  ReferenceTarget,
} from "../domain/types";
import { resolveCanvasModeConfiguration } from "../modes";
import type { CanvasMode } from "../domain/types";
import {
  createDefaultCanvasInteractionStrategyRegistry,
  type CanvasInteractionStrategyRegistry,
} from "../interactions/strategies";
import type { CanvasAssetResolver, ReferenceResolver } from "../references";
import { CanvasScene } from "./CanvasScene";
import { CanvasStatusOverlay } from "./CanvasStatusOverlay";
import { defaultToolProps } from "../node-style";
import { CanvasToolOptions, STYLED_TOOLS } from "./CanvasToolOptions";
import { CanvasToolbar } from "./CanvasToolbar";
import { CanvasOutline } from "./CanvasOutline";
import { CanvasEdgeInspector } from "./CanvasEdgeInspector";
import { CanvasLayersPanel } from "./CanvasLayersPanel";
import { CanvasMinimap } from "./CanvasMinimap";
import { CanvasSelectionToolbar } from "./CanvasSelectionToolbar";
import { CanvasPropertiesPanel } from "./CanvasPropertiesPanel";
import {
  CanvasI18nProvider,
  resolveCanvasMessages,
  type CanvasLocale,
  type CanvasMessages,
} from "./i18n";
import { useCanvasSnapshot, useReferenceResolutions } from "./hooks";
import {
  useCanvasInteractions,
  type CanvasImageInsert,
  type CanvasReferenceInsert,
  type CanvasRequestContext,
} from "./useCanvasInteractions";

export type {
  CanvasImageInsert,
  CanvasReferenceInsert,
  CanvasRequestContext,
} from "./useCanvasInteractions";

export interface CanvasBoardProps {
  editor: CanvasEditor;
  referenceResolver?: ReferenceResolver;
  assetResolver?: CanvasAssetResolver;
  loadState?: CanvasLoadState;
  readOnly?: boolean;
  initialTool?: CanvasTool;
  initialToolOptionsVisible?: boolean;
  showToolbar?: boolean;
  showViewportStatus?: boolean;
  ariaLabel?: string;
  locale?: CanvasLocale;
  messages?: Partial<CanvasMessages>;
  initialOutlineVisible?: boolean;
  outlineVisible?: boolean;
  onOutlineVisibleChange?(visible: boolean): void;
  initialMinimapVisible?: boolean;
  minimapVisible?: boolean;
  onMinimapVisibleChange?(visible: boolean): void;
  initialLayersVisible?: boolean;
  layersVisible?: boolean;
  onLayersVisibleChange?(visible: boolean): void;
  className?: string;
  style?: CSSProperties;
  onChangeBatch?(batch: CanvasChangeBatch): void;
  onReferenceActivate?(target: ReferenceTarget): void;
  onRequestImage?(
    context: CanvasRequestContext,
  ): Promise<CanvasImageInsert | null>;
  onRequestReference?(
    context: CanvasRequestContext,
  ): Promise<CanvasReferenceInsert | null>;
  referenceRefreshKey?: string | number;
  interactionStrategyRegistry?: CanvasInteractionStrategyRegistry;
  onModeChange?(mode: CanvasMode): void;
}

export function CanvasBoard({
  editor,
  referenceResolver,
  assetResolver,
  loadState,
  readOnly,
  initialTool = "select",
  initialToolOptionsVisible = false,
  showToolbar = true,
  showViewportStatus = true,
  ariaLabel,
  locale = "zh-CN",
  messages: messageOverrides,
  initialOutlineVisible = false,
  outlineVisible: controlledOutlineVisible,
  onOutlineVisibleChange,
  initialMinimapVisible = true,
  minimapVisible: controlledMinimapVisible,
  onMinimapVisibleChange,
  initialLayersVisible = false,
  layersVisible: controlledLayersVisible,
  onLayersVisibleChange,
  className,
  style,
  onChangeBatch,
  onReferenceActivate,
  onRequestImage,
  onRequestReference,
  referenceRefreshKey,
  interactionStrategyRegistry,
  onModeChange,
}: CanvasBoardProps) {
  const messages = useMemo(
    () => resolveCanvasMessages(locale, messageOverrides),
    [locale, messageOverrides],
  );
  const snapshot = useCanvasSnapshot(editor);
  const [toolStyles, setToolStyles] = useState<Partial<Record<CanvasTool, Record<string, import("../domain/types").JsonValue>>>>({});
  const [keepTool, setKeepTool] = useState(false);
  const [toolOptionsVisible, setToolOptionsVisible] = useState(initialToolOptionsVisible);
  const [tool, setTool] = useState<CanvasTool>(initialTool);
  const [internalOutlineVisible, setInternalOutlineVisible] = useState(
    initialOutlineVisible,
  );
  const [internalMinimapVisible, setInternalMinimapVisible] = useState(
    initialMinimapVisible,
  );
  const [internalLayersVisible, setInternalLayersVisible] = useState(
    initialLayersVisible,
  );
  const [engineeringTarget, setEngineeringTarget] = useState<{ nodeId?: string }>();
  const [editingNodeId, setEditingNodeId] = useState<EntityId>();
  const [propertiesVisible, setPropertiesVisible] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ readonly nodeId: EntityId; readonly x: number; readonly y: number }>();
  const longPress = useRef<{ readonly pointerId: number; readonly nodeId: EntityId; readonly startX: number; readonly startY: number; readonly timer: ReturnType<typeof setTimeout> } | undefined>(undefined);
  const [boardFocused, setBoardFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const defaultStrategyRegistry = useMemo(
    () => createDefaultCanvasInteractionStrategyRegistry(),
    [],
  );
  const strategyRegistry =
    interactionStrategyRegistry ?? defaultStrategyRegistry;
  const mode = resolveCanvasModeConfiguration(snapshot.canvas.mode);
  const effectiveLoadState: CanvasLoadState =
    loadState ??
    (snapshot.canvas.nodes.length === 0
      ? { status: "empty", message: messages.emptyHint }
      : { status: "ready" });
  const outlineVisible = controlledOutlineVisible ?? internalOutlineVisible;
  const minimapVisible = controlledMinimapVisible ?? internalMinimapVisible;
  const layersVisible = controlledLayersVisible ?? internalLayersVisible;
  const toggleOutline = () => {
    const nextVisible = !outlineVisible;
    if (controlledOutlineVisible === undefined) {
      setInternalOutlineVisible(nextVisible);
    }
    onOutlineVisibleChange?.(nextVisible);
  };
  const toggleMinimap = () => {
    const nextVisible = !minimapVisible;
    if (controlledMinimapVisible === undefined) setInternalMinimapVisible(nextVisible);
    onMinimapVisibleChange?.(nextVisible);
  };
  const toggleLayers = () => {
    const nextVisible = !layersVisible;
    if (controlledLayersVisible === undefined) setInternalLayersVisible(nextVisible);
    onLayersVisibleChange?.(nextVisible);
  };

  useEffect(() => {
    if (readOnly !== undefined) editor.setReadOnly(readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (
      ((tool === "draw" || tool === "eraser") && !mode.features.freehand) ||
      (tool === "frame" && !mode.features.frames) ||
      (tool === "arrow" && !mode.features.connections) ||
      (tool === "reference" && !mode.features.references)
    ) {
      setTool("select");
    }
  }, [
    mode.features.connections,
    mode.features.frames,
    mode.features.freehand,
    mode.features.references,
    tool,
  ]);

  useEffect(() => {
    if (onChangeBatch === undefined) return;
    return editor.subscribeToChangeBatches(onChangeBatch);
  }, [editor, onChangeBatch]);

  useEffect(() => {
    const container = containerRef.current;
    if (container === null) return;
    const updateSize = () => {
      const bounds = container.getBoundingClientRect();
      editor.setViewportSize(bounds.width, bounds.height);
    };
    updateSize();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(updateSize);
    observer.observe(container);
    return () => observer.disconnect();
  }, [editor]);

  const viewportBounds = useMemo(() => {
    const { view } = snapshot;
    if (view.viewportWidth === 0 || view.viewportHeight === 0) {
      return undefined;
    }
    const overscan = 300 / view.zoom;
    return {
      minX: -view.panX / view.zoom - overscan,
      minY: -view.panY / view.zoom - overscan,
      maxX: (view.viewportWidth - view.panX) / view.zoom + overscan,
      maxY: (view.viewportHeight - view.panY) / view.zoom + overscan,
    };
  }, [snapshot.view]);
  const visibleNodes = useMemo(
    () =>
      (viewportBounds === undefined
        ? snapshot.canvas.nodes
        : editor.queryNodes(viewportBounds)).filter((node) => node.props.hidden !== true),
    [editor, snapshot.canvas.nodes, snapshot.revision, viewportBounds],
  );
  const visibleEdges = useMemo(
    () => {
      const hiddenIds = new Set(snapshot.canvas.nodes.filter((node) => node.props.hidden === true).map((node) => node.id));
      return (viewportBounds === undefined
        ? snapshot.canvas.edges
        : editor.queryEdges(viewportBounds)).filter((edge) => !hiddenIds.has(edge.sourceNodeId) && !hiddenIds.has(edge.targetNodeId));
    },
    [editor, snapshot.canvas.edges, snapshot.revision, viewportBounds],
  );

  const orderedVisibleNodes = useMemo(
    () =>
      [...visibleNodes].sort((left, right) =>
        left.geometry.zIndex.localeCompare(right.geometry.zIndex),
      ),
    [visibleNodes],
  );
  const referenceState = useReferenceResolutions(
    orderedVisibleNodes,
    referenceResolver,
    referenceRefreshKey,
  );
  const nodeById = useMemo(
    () => new Map(snapshot.canvas.nodes.map((node) => [node.id, node])),
    [snapshot.canvas.nodes],
  );
  const disabledTools = useMemo(
    () =>
      new Set<CanvasTool>([
        ...(onRequestImage === undefined || assetResolver === undefined
          ? (["image"] as const)
          : []),
        ...(onRequestReference === undefined || referenceResolver === undefined
          ? (["reference"] as const)
          : []),
      ]),
    [assetResolver, onRequestImage, onRequestReference, referenceResolver],
  );
  const disabledToolReasons = useMemo(
    () => ({
      ...(disabledTools.has("image")
        ? {
            image:
              assetResolver === undefined
                ? messages.imageResolverUnavailable
                : messages.imagePickerUnavailable,
          }
        : {}),
      ...(disabledTools.has("reference")
        ? {
            reference:
              referenceResolver === undefined
                ? messages.referenceResolverUnavailable
                : messages.referencePickerUnavailable,
          }
        : {}),
    }),
    [assetResolver, disabledTools, messages, referenceResolver],
  );
  const creationProps = toolStyles[tool] ?? { ...defaultToolProps(tool), ...(snapshot.canvas.settings.handwriting === true ? { fontFamily: HANDWRITING_FONT, fontSize: 24 } : {}) };
  const interactions = useCanvasInteractions({
    creationProps,
    keepTool,
    editor,
    snapshot,
    tool,
    setTool,
    nodeById,
    containerRef,
    svgRef,
    setEditingNodeId,
    messages,
    assetResolverAvailable: assetResolver !== undefined,
    referenceResolverAvailable: referenceResolver !== undefined,
    onRequestImage,
    onRequestReference,
    strategyRegistry,
  });

  const engineeringNode = engineeringTarget?.nodeId ? snapshot.canvas.nodes.find((node) => node.id === engineeringTarget.nodeId) : undefined;
  const editingDiagram = snapshot.canvas.nodes.find((node) => snapshot.selection.nodeIds.has(node.id) && node.type === "diagram");
  const openDiagram = (nodeId?: string) => { setTool("select"); setEngineeringTarget(nodeId ? { nodeId } : {}); };

  const cancelLongPress = () => {
    if (longPress.current) clearTimeout(longPress.current.timer);
    longPress.current = undefined;
  };
  const beginLongPress = (event: ReactPointerEvent<HTMLDivElement>) => {
    setContextMenu(undefined);
    if (event.pointerType !== "touch" || snapshot.readOnly || tool !== "select") return;
    const target = event.target as Element | null;
    const element = target?.closest?.("[data-canvas-node-id]") as HTMLElement | null;
    const nodeId = element?.dataset.canvasNodeId;
    if (!nodeId) return;
    cancelLongPress();
    const { clientX: startX, clientY: startY, pointerId } = event;
    const timer = setTimeout(() => {
      interactions.cancelPointer(event as unknown as ReactPointerEvent<SVGSVGElement>);
      editor.setSelection({ nodeIds: [nodeId], primaryId: nodeId });
      const bounds = containerRef.current?.getBoundingClientRect();
      if (bounds) setContextMenu({ nodeId, x: Math.min(bounds.width - 190, Math.max(8, startX - bounds.left)), y: Math.min(bounds.height - 220, Math.max(8, startY - bounds.top)) });
      globalThis.navigator?.vibrate?.(18);
      longPress.current = undefined;
    }, 520);
    longPress.current = { pointerId, nodeId, startX, startY, timer };
  };
  const moveLongPress = (event: ReactPointerEvent<HTMLDivElement>) => {
    const gesture = longPress.current;
    if (!gesture || gesture.pointerId !== event.pointerId) return;
    if (Math.hypot(event.clientX - gesture.startX, event.clientY - gesture.startY) > 10) cancelLongPress();
  };

  return (
    <CanvasI18nProvider locale={locale} messages={messages}>
      <div
        ref={containerRef}
        className={className}
        tabIndex={0}
        role="application"
        aria-label={ariaLabel ?? messages.boardLabel}
        aria-readonly={snapshot.readOnly}
        onPointerDownCapture={beginLongPress}
        onPointerMoveCapture={moveLongPress}
        onPointerUpCapture={cancelLongPress}
        onPointerCancelCapture={cancelLongPress}
        onKeyDown={interactions.handleKeyboard}
        onFocus={(event) => {
          if (event.currentTarget === event.target) setBoardFocused(true);
        }}
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setBoardFocused(false);
          }
        }}
        style={{
          position: "relative",
          containerType: "inline-size",
          containerName: "canvas-board",
          width: "100%",
          minHeight: 480,
          overflow: "hidden",
          color: "#172033",
          background: "#f8fafc",
          outline: boardFocused ? "3px solid #0f766e" : "none",
          outlineOffset: boardFocused ? 2 : undefined,
          ...style,
        }}
      >
        {showToolbar && (
          <FloatingToolbar storageKey={ariaLabel === "手写讲义画布" ? "handwriting" : "canvas"} label="画板工具栏" width={530}>
          <CanvasToolbar
            editor={editor}
            tool={tool}
            onToolChange={setTool}
            mode={mode}
            outlineVisible={outlineVisible}
            onToggleOutline={toggleOutline}
            minimapVisible={minimapVisible}
            onToggleMinimap={toggleMinimap}
            layersVisible={layersVisible}
            onToggleLayers={toggleLayers}
            disabledTools={disabledTools}
            disabledToolReasons={disabledToolReasons}
            onCreateDiagram={() => openDiagram()}
            onDuplicate={interactions.duplicateSelection}
            onFitToScreen={interactions.fitToScreen}
            onFitToSelection={interactions.fitToSelection}
            onModeChange={
              onModeChange ??
              ((nextMode) => {
                editor.dispatch({ type: "set-mode", mode: nextMode });
              })
            }
          />
        {!snapshot.readOnly && STYLED_TOOLS.has(tool) ? <><button type="button" className="canvas-tool-settings-toggle" aria-label={toolOptionsVisible ? "收起画笔与样式设置" : "打开画笔与样式设置"} aria-expanded={toolOptionsVisible} onClick={() => setToolOptionsVisible((visible) => !visible)}>{toolOptionsVisible ? "收起设置" : "画笔与样式"}</button>{toolOptionsVisible && <CanvasToolOptions tool={tool} value={creationProps} keepTool={keepTool} onKeepToolChange={setKeepTool} onChange={(patch) => setToolStyles((styles) => ({ ...styles, [tool]: { ...creationProps, ...patch } }))} />}</> : null}
        {!snapshot.readOnly && tool === "eraser" && <aside className="canvas-eraser-hint">擦除整条笔画；按 D 返回书写。</aside>}
          </FloatingToolbar>
        )}
        <CanvasScene
          editor={editor}
          snapshot={snapshot}
          svgRef={svgRef}
          tool={tool}
          orderedVisibleNodes={orderedVisibleNodes}
          visibleEdges={visibleEdges}
          nodeById={nodeById}
          referenceState={referenceState}
          editingNodeId={editingNodeId}
          preview={interactions.preview}
          creationProps={creationProps}
          assetResolver={assetResolver}
          onPointerDown={interactions.beginBackgroundGesture}
          onPointerMove={interactions.movePointer}
          onPointerUp={interactions.endPointer}
          onPointerCancel={interactions.cancelPointer}
          onWheel={interactions.handleWheel}
          onEditDiagram={(node) => openDiagram(node.id)}
          onNodePointerDown={interactions.beginNodeGesture}
          onNodeContextMenu={(node, event) => {
            event.preventDefault();
            editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
            const bounds = containerRef.current?.getBoundingClientRect();
            if (bounds) setContextMenu({ nodeId: node.id, x: Math.min(bounds.width - 190, Math.max(8, event.clientX - bounds.left)), y: Math.min(bounds.height - 220, Math.max(8, event.clientY - bounds.top)) });
          }}
          onNodeResizePointerDown={interactions.beginResizeGesture}
          onNodeRotatePointerDown={interactions.beginRotationGesture}
          onNodeResizeKeyDown={interactions.resizeNodeWithKeyboard}
          onNodeRotateKeyDown={interactions.rotateNodeWithKeyboard}
          onEdgeReconnectPointerDown={
            interactions.beginEdgeReconnectGesture
          }
          onEditingNodeChange={setEditingNodeId}
          onReferenceActivate={onReferenceActivate}
        />
        {outlineVisible && (
          <CanvasOutline
            editor={editor}
            snapshot={snapshot}
            onNodeActivate={(nodeId) => {
              const target = [...containerRef.current?.querySelectorAll<
                SVGGElement
              >("[data-canvas-node-id]") ?? []].find(
                (element) => element.dataset.canvasNodeId === nodeId,
              );
              target?.focus();
            }}
          />
        )}
        {layersVisible ? (
          <CanvasLayersPanel
            editor={editor}
            snapshot={snapshot}
            onClose={toggleLayers}
          />
        ) : null}
        {minimapVisible ? <CanvasMinimap editor={editor} snapshot={snapshot} /> : null}
        <CanvasSelectionToolbar
          editor={editor}
          snapshot={snapshot}
          onDuplicate={interactions.duplicateSelection}
          onShowProperties={() => setPropertiesVisible(true)}
          {...(snapshot.selection.nodeIds.size === 1 && editingDiagram ? { onEditDiagram: () => openDiagram(editingDiagram.id) } : {})}
        />
        {engineeringTarget && <Suspense fallback={<div role="status" style={{ position: "absolute", top: 80, left: 20, background: "white", padding: 12 }}>正在打开工程图编辑器…</div>}><EngineeringDiagramEditor
          {...(engineeringNode ? { source: String(engineeringNode.props.source ?? ""), templateId: String(engineeringNode.props.templateId ?? "") } : {})}
          readOnly={snapshot.readOnly || engineeringNode?.props.locked === true}
          onClose={() => setEngineeringTarget(undefined)}
          onApply={(source, templateId, rendered) => {
            const current = editor.getSnapshot();
            if (current.readOnly) return;
            if (engineeringTarget.nodeId) {
              const node = current.canvas.nodes.find((candidate) => candidate.id === engineeringTarget.nodeId);
              if (!node || node.props.locked === true) throw new Error("此图表已被删除或锁定。");
              editor.dispatch({ type: "update-node", id: node.id, patch: { props: { source, templateId, title: diagramTemplate(templateId).title } } });
            } else {
              const width = Math.max(200, Math.min(620, (current.view.viewportWidth - 80) / current.view.zoom));
              const height = Math.max(180, Math.min(580, (current.view.viewportHeight - 190) / current.view.zoom, width * rendered.height / rendered.width + 40));
              const node = createCanvasNode({ type: "diagram", geometry: { x: (current.view.viewportWidth / 2 - current.view.panX) / current.view.zoom - width / 2, y: ((current.view.viewportHeight + 20) / 2 - current.view.panY) / current.view.zoom - height / 2, width, height, zIndex: `a${Date.now().toString(36)}` }, props: { source, templateId, title: diagramTemplate(templateId).title } });
              editor.dispatch({ type: "create-node", node });
              editor.setSelection({ nodeIds: [node.id], primaryId: node.id });
            }
          }} /></Suspense>}
        {propertiesVisible ? <CanvasPropertiesPanel editor={editor} snapshot={snapshot} onClose={() => setPropertiesVisible(false)} /> : null}
        <CanvasEdgeInspector editor={editor} snapshot={snapshot} />
        {contextMenu ? <CanvasNodeContextMenu editor={editor} snapshot={snapshot} nodeId={contextMenu.nodeId} x={contextMenu.x} y={contextMenu.y} onClose={() => setContextMenu(undefined)} onDuplicate={interactions.duplicateSelection} onProperties={() => { setContextMenu(undefined); setPropertiesVisible(true); }} /> : null}
        {interactions.requestError !== undefined && (
          <div
            role="alert"
            style={{
              position: "absolute",
              zIndex: 25,
              top: showToolbar ? 72 : 12,
              left: "50%",
              display: "flex",
              alignItems: "center",
              gap: 8,
              maxWidth: "calc(100% - 24px)",
              padding: "8px 10px 8px 12px",
              border: "1px solid #fecaca",
              borderRadius: 9,
              color: "#991b1b",
              background: "#fff7f7",
              boxShadow: "0 6px 24px rgba(127,29,29,.12)",
              transform: "translateX(-50%)",
            }}
          >
            <span>{interactions.requestError}</span>
            <button
              type="button"
              aria-label={messages.dismissError}
              onClick={interactions.clearRequestError}
              style={{
                width: 32,
                height: 32,
                border: 0,
                borderRadius: 6,
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <span aria-hidden="true">×</span>
            </button>
          </div>
        )}
        <CanvasStatusOverlay
          showViewportStatus={showViewportStatus}
          loadState={effectiveLoadState}
          mode={mode}
          zoom={snapshot.view.zoom}
        />
      </div>
    </CanvasI18nProvider>
  );
}

function CanvasNodeContextMenu({ editor, snapshot, nodeId, x, y, onClose, onDuplicate, onProperties }: { readonly editor: CanvasEditor; readonly snapshot: ReturnType<CanvasEditor["getSnapshot"]>; readonly nodeId: EntityId; readonly x: number; readonly y: number; readonly onClose: () => void; readonly onDuplicate: () => void; readonly onProperties: () => void }) {
  const node = snapshot.canvas.nodes.find((entry) => entry.id === nodeId);
  if (!node) return null;
  const action = (run: () => void) => { run(); onClose(); };
  return <div role="menu" aria-label="节点上下文操作" style={{ position: "absolute", zIndex: 30, left: x, top: y, display: "grid", minWidth: 182, padding: 6, border: "1px solid #cbd5e1", borderRadius: 11, background: "rgba(255,255,255,.99)", boxShadow: "0 14px 36px rgba(15,23,42,.2)" }}>
    <button type="button" role="menuitem" onClick={() => action(onDuplicate)}>创建副本 <kbd>⌘D</kbd></button>
    <button type="button" role="menuitem" onClick={onProperties}>属性检查器</button>
    <button type="button" role="menuitem" onClick={() => action(() => editor.dispatch({ type: "update-node", id: node.id, patch: { props: { locked: node.props.locked !== true } } }))}>{node.props.locked === true ? "解锁节点" : "锁定节点"}</button>
    <button type="button" role="menuitem" onClick={() => action(() => editor.dispatch({ type: "update-node", id: node.id, patch: { props: { hidden: true } } }))}>隐藏节点</button>
    <button type="button" role="menuitem" disabled={node.props.locked === true} onClick={() => action(() => editor.dispatch({ type: "reorder-nodes", nodeIds: [node.id], operation: "bring-front" }))}>置于顶层</button>
    <button type="button" role="menuitem" disabled={node.props.locked === true} onClick={() => action(() => editor.dispatch({ type: "delete-nodes", ids: [node.id] }))} style={{ color: "#b91c1c" }}>删除</button>
  </div>;
}
